// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  X, Volume2, Copy, Info, Sun, Moon,
  ClipboardCheck, AudioLines, StopCircle, AlertTriangle, MessageSquare, RefreshCw,
} from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Branding } from '@qh/design-tokens/branding'
import { ChatSkeleton } from '@/components/Skeleton'
import { ComposerButton } from '@/components/ComposerButton'
import { MicPermissionModal } from '@/components/MicPermissionModal'
import { VoiceModeOverlay, isOverlayVoiceState } from '@/components/VoiceModeOverlay'
import { AppointmentCard, providerLabel } from '@/components/AppointmentCard'
import { useTheme } from '@/context/theme-context'
import { cn } from '@/lib/utils'
import { isKioskSession } from '@/lib/kiosk-mode'
import { clearSession } from '@/lib/session'
import { useIntakeConversation } from '@/intake/use-intake-conversation'
import type { IntakeMessage, IntakeVisit } from '@/intake/types'

function TypingIndicator() {
  const { t } = useTranslation()
  return (
    <div className="py-4 animate-fade-up">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center flex-shrink-0 shadow-sm shadow-primary/20">
          <div style={{ filter: 'brightness(0) invert(1)' }}>
            <Logo variant="mark" className="w-5 h-5" aria-hidden />
          </div>
        </div>
        <p className="text-[13px] font-semibold text-foreground leading-none">{t('chat.aiName')}</p>
      </div>
      <div className="flex items-center gap-1.5 px-1">
        <span className="w-2 h-2 rounded-full bg-primary animate-dot-1" />
        <span className="w-2 h-2 rounded-full bg-primary animate-dot-2" />
        <span className="w-2 h-2 rounded-full bg-primary animate-dot-3" />
      </div>
    </div>
  )
}

// Pre-conversation channel picker: Text (recommended) or Voice. Only rendered when
// voice is available; both channels continue the same conversation server-side.
function ChannelChoiceScreen({ onText, onVoice }: { onText: () => void; onVoice: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-6 py-10 bg-gradient-to-br from-[#011829] via-[#012540] to-[#011829] relative overflow-hidden">
      <div className="absolute -top-24 left-[10%] w-[500px] h-[500px] rounded-full bg-[#006EF9]/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-[5%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#02C7C3]/8 blur-[120px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
        <div className="mb-8" style={{ filter: 'brightness(0) invert(1)' }}>
          <Logo variant="mark" className="h-8 w-auto" aria-hidden />
        </div>
        <h1 className="text-2xl font-bold text-white text-center mb-2">{t('chat.channelTitle')}</h1>
        <p className="text-sm text-white/60 text-center mb-8">{t('chat.channelSubtitle')}</p>

        <button
          onClick={onText}
          className="w-full flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 mb-3 text-left hover:bg-white/10 active:scale-[0.99] transition-all"
        >
          <MessageSquare className="h-6 w-6 text-white/80 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-base font-semibold text-white">{t('chat.channelText')}</span>
            <p className="text-[13px] text-white/60 mt-0.5">{t('chat.channelTextDesc')}</p>
          </div>
        </button>

        <button
          onClick={onVoice}
          className="w-full flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-left hover:bg-white/10 active:scale-[0.99] transition-all"
        >
          <AudioLines className="h-6 w-6 text-white/80 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-base font-semibold text-white">{t('chat.channelVoice')}</span>
            <p className="text-[13px] text-white/60 mt-0.5">{t('chat.channelVoiceDesc')}</p>
          </div>
        </button>
      </div>
    </div>
  )
}

// Shown in-thread after a voice call drops before completing. Never a dead end: the text
// composer stays available below and Restart voice reconnects.
function VoiceDroppedBanner({
  onRestart,
  onDismiss,
  className,
}: {
  onRestart: () => void
  onDismiss: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('rounded-2xl border border-border bg-secondary/60 px-4 py-3.5', className)}>
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <AudioLines className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-foreground">{t('voice.droppedTitle')}</p>
          <p className="text-[12px] leading-snug text-muted-foreground">{t('voice.droppedBody')}</p>
        </div>
        <button
          onClick={onDismiss}
          aria-label={t('voice.droppedDismiss')}
          className="shrink-0 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <button
        onClick={onRestart}
        className="mt-2.5 flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#006EF9] to-[#02C7C3] text-[13px] font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <RefreshCw className="h-4 w-4" />
        {t('voice.restartVoice')}
      </button>
    </div>
  )
}

function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

function UserMessage({ msg, patientName }: { msg: IntakeMessage; patientName: string }) {
  return (
    <div className="py-4 border-b border-border animate-fade-up">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#033C85] to-[#006EF9] flex items-center justify-center flex-shrink-0 shadow-sm shadow-primary/20">
          <span className="text-white text-[12px] font-bold leading-none">{initialsOf(patientName)}</span>
        </div>
        <div>
          <p className="text-[13px] font-semibold text-foreground leading-none">{patientName}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{msg.timestamp}</p>
        </div>
      </div>
      <p className="text-[15px] text-foreground leading-relaxed mb-3">{msg.content}</p>
      <div className="flex items-center gap-3">
        <button className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Copy">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function AssistantMessage({ msg }: { msg: IntakeMessage }) {
  const { t } = useTranslation()
  const lines = msg.content.split('\n\n')
  return (
    <div className="py-4 animate-fade-up">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center flex-shrink-0 shadow-sm shadow-primary/20">
          <div style={{ filter: 'brightness(0) invert(1)' }}>
            <Logo variant="mark" className="w-5 h-5" aria-hidden />
          </div>
        </div>
        <div>
          <p className="text-[13px] font-semibold text-foreground leading-none">{t('chat.aiName')}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{msg.timestamp}</p>
        </div>
      </div>
      {msg.heading && <h2 className="text-[22px] font-bold text-foreground mb-3">{msg.heading}</h2>}
      <div className="space-y-3 mb-4">
        {lines.map((line, i) => {
          if (line.startsWith('**') && line.endsWith('**')) {
            return (
              <p key={i} className="text-[15px] font-bold text-foreground leading-relaxed">
                {line.slice(2, -2)}
              </p>
            )
          }
          return (
            <p key={i} className="text-[15px] text-foreground leading-relaxed">
              {line}
            </p>
          )
        })}
      </div>
      <div className="flex items-center gap-3">
        <button className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Read aloud">
          <Volume2 className="w-4 h-4" />
        </button>
        <button className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Copy">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function WelcomeEmptyState() {
  const { t } = useTranslation()
  return (
    <div className="py-10 text-center animate-fade-up">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center mb-4">
        <div style={{ filter: 'brightness(0) invert(1)' }}>
          <Logo variant="mark" className="w-7 h-7" aria-hidden />
        </div>
      </div>
      <h2 className="text-[18px] font-bold text-foreground mb-2">{t('chat.welcomeTitle')}</h2>
      <p className="text-[14px] text-muted-foreground max-w-xs mx-auto leading-relaxed">{t('chat.welcomeBody')}</p>
    </div>
  )
}

function MessageList({
  messages,
  isTyping,
  patientName,
  visit,
}: {
  messages: IntakeMessage[]
  isTyping: boolean
  patientName: string
  visit: IntakeVisit | null
}) {
  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  return (
    <>
      <AppointmentCard visit={visit} />
      {messages.length === 0 && !isTyping && <WelcomeEmptyState />}
      {messages.map((msg) =>
        msg.role === 'user' ? (
          <UserMessage key={msg.id} msg={msg} patientName={patientName} />
        ) : (
          <AssistantMessage key={msg.id} msg={msg} />
        ),
      )}
      {isTyping && <TypingIndicator />}
      <div ref={bottomRef} />
    </>
  )
}

interface InputBarProps {
  input: string
  voiceAvailable: boolean
  onChange: (val: string) => void
  onSend: () => void
  onVoiceClick: () => void
  className?: string
}

function InputBar({ input, voiceAvailable, onChange, onSend, onVoiceClick, className }: InputBarProps) {
  const { t } = useTranslation()
  return (
    <div className={cn('bg-card border-t border-border px-4 pt-2 pb-6', className)}>
      <div className="flex items-center gap-2 mb-2">
        <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <p className="text-[11px] text-muted-foreground">{t('chat.aiDisclaimerShort')}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input.trim()) onSend()
          }}
          placeholder={t('chat.inputPlaceholder')}
          className="flex-1 h-12 px-4 rounded-2xl border border-border bg-secondary dark:bg-input-bg text-[15px] text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
        />
        <ComposerButton
          hasText={input.trim().length > 0}
          voiceAvailable={voiceAvailable}
          onSend={onSend}
          onVoice={onVoiceClick}
        />
      </div>
    </div>
  )
}

export function ChatScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const [input, setInput] = useState('')
  const [showStopConfirm, setShowStopConfirm] = useState(false)

  const {
    phase, messages, voiceState, patientName, isSending, voiceSupported, muted,
    requestVoice, confirmVoice, cancelVoice, endVoice, toggleMute, switchToText, sendText,
    retryInit, permissionDenied, liveTranscript, liveCaption, visit, isComplete, reviewSummary,
    voiceDropped, dismissVoiceDropped,
  } = useIntakeConversation()

  // Kiosk is a captive shared-device flow: the exit hands the device back to staff (with a
  // clean session teardown), never a raw back-nav that strands the session for the next patient.
  const kiosk = isKioskSession()
  const exitLabel = kiosk ? t('chat.handBack') : t('chat.stopChat')
  const confirmExit = () => {
    endVoice()
    if (kiosk) {
      clearSession()
      navigate('/kiosk/done')
    } else {
      navigate('/')
    }
  }

  const displayName = patientName || t('chat.youFallback')
  const provider = providerLabel(visit, t('chat.providerFallback'))

  // Channel selection. The patient picks Text or Voice before the conversation
  // starts (both drive the SAME conversationId server-side — switching mid-way
  // works). Default is TEXT for now (voice has a latency redesign in flight); the
  // Voice option only appears when the backend reports voice available
  // (voiceSupported = voiceEnabled + supported browser). Null = not yet chosen.
  const [channel, setChannel] = useState<'text' | 'voice' | null>(null)

  function chooseVoice() {
    setChannel('voice')
    // Kiosk already consented the mic → start the call directly; the self-service
    // browser path opens the mic explainer so the "Allow" tap is the user gesture.
    if (isKioskSession()) confirmVoice()
    else requestVoice()
  }

  // Reconnect after a drop — same gesture/permission path as the composer's voice entry.
  function restartVoice() {
    if (isKioskSession()) confirmVoice()
    else requestVoice()
  }

  function handleSend() {
    const value = input.trim()
    if (!value) return
    sendText(value)
    setInput('')
  }

  const isLoading = phase === 'initializing'
  // The review CTA appears only when the backend reports the intake complete — not on a
  // message-count guess and not on the assistant's farewell wording.
  const showCompleteCta = isComplete
  // Progress reads to full once complete; otherwise a bounded estimate off turn count.
  const intakePct = isComplete ? 100 : Math.min(95, Math.floor((messages.length / 10) * 100))
  const voiceAvailable = voiceSupported && voiceState !== 'connecting'

  if (phase === 'error') {
    return (
      <div className="flex flex-col h-screen items-center justify-center bg-background px-6 text-center">
        <AlertTriangle className="w-10 h-10 text-red-500 mb-4" />
        <h1 className="text-lg font-semibold text-foreground mb-2">{t('chat.errorTitle')}</h1>
        <p className="text-sm text-muted-foreground mb-6 max-w-xs">{t('chat.errorBody')}</p>
        <button
          onClick={retryInit}
          className="h-11 px-6 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] hover:opacity-90 transition-opacity"
        >
          {t('chat.retry')}
        </button>
      </div>
    )
  }

  // Channel selector — shown once the conversation is ready AND voice is available.
  // If voice isn't available, there's nothing to choose, so we fall straight into
  // the (text) conversation. Default/recommended is Text.
  if (phase === 'ready' && channel === null && voiceSupported) {
    return <ChannelChoiceScreen onText={() => setChannel('text')} onVoice={chooseVoice} />
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* ── Mobile layout ── */}
      <div className="flex flex-col flex-1 md:hidden min-h-0">
        <div className="flex-shrink-0 bg-gradient-to-r from-[#012031] via-[#033C85] to-[#006EF9] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#02C7C3]/20 blur-2xl" />
          </div>
          <div className="relative z-10 flex items-center justify-between px-4 py-3">
            <div className="w-10 h-10 rounded-full bg-white/15 border border-white/25 flex items-center justify-center">
              <div style={{ filter: 'brightness(0) invert(1)' }}>
                <Logo variant="mark" className="w-5 h-5" aria-hidden />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowStopConfirm(true)}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-red-500/30 transition-colors"
                aria-label={exitLabel}
              >
                <StopCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
          {!isLoading && (
            <div className="relative z-10 px-4 pb-2.5">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-semibold text-white/60 uppercase tracking-widest">{t('chat.intakeProgress')}</p>
                <p className="text-[10px] font-bold text-white/80">{intakePct}%</p>
              </div>
              <div className="h-1 w-full bg-white/15 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#02C7C3] to-white/80 rounded-full transition-all duration-700"
                  style={{ width: `${intakePct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 bg-card overflow-hidden flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-5">
            {isLoading ? (
              <ChatSkeleton />
            ) : (
              <MessageList messages={messages} isTyping={isSending} patientName={displayName} visit={visit} />
            )}
          </div>

          {showCompleteCta && (
            <div className="flex-shrink-0 mx-4 mb-2 rounded-2xl border border-primary/20 bg-gradient-to-r from-[#006EF9]/10 to-[#02C7C3]/10 px-4 py-3.5">
              <div className="mb-2.5 flex items-start gap-2.5">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <ClipboardCheck className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-foreground">{t('chat.reviewCompleteTitle')}</p>
                  <p className="text-[12px] text-muted-foreground leading-snug">
                    {visit?.providerName
                      ? t('chat.reviewCompleteBody', { provider })
                      : t('chat.reviewCompleteBodyGeneric')}
                  </p>
                </div>
              </div>
              <button
                onClick={reviewSummary}
                className="h-9 w-full rounded-xl bg-gradient-to-r from-[#006EF9] to-[#02C7C3] text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                {t('chat.reviewSummaryCta')}
              </button>
            </div>
          )}

          {voiceDropped && (
            <VoiceDroppedBanner
              onRestart={restartVoice}
              onDismiss={dismissVoiceDropped}
              className="flex-shrink-0 mx-4 mb-2"
            />
          )}

          <InputBar
            input={input}
            voiceAvailable={voiceAvailable}
            onChange={setInput}
            onSend={handleSend}
            onVoiceClick={requestVoice}
            className="flex-shrink-0"
          />
        </div>
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden md:flex flex-1 overflow-hidden min-h-0">
        <div className="w-64 xl:w-72 relative flex flex-col px-6 py-8 flex-shrink-0 overflow-hidden bg-gradient-to-b from-[#012031] via-[#033C85] to-[#006EF9]">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/15 blur-3xl" />
            <div className="absolute top-1/3 -left-10 w-[140px] h-[140px] rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative z-10 flex flex-col flex-1">
            <div className="w-12 h-12 rounded-full bg-white/15 border border-white/25 flex items-center justify-center mb-4 shadow-sm">
              <div style={{ filter: 'brightness(0) invert(1)' }}>
                <Logo variant="mark" className="w-7 h-7" aria-hidden />
              </div>
            </div>
            <h2 className="text-[15px] font-bold text-white mb-1">{t('chat.aiName')}</h2>
            <p className="text-[12px] text-white/60 leading-relaxed">{t('chat.aiRole')}</p>
            <div className="mt-5 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/15">
              <p className="text-[11px] text-white/80 leading-relaxed font-medium">{t('chat.aiDisclaimer')}</p>
            </div>

            {showCompleteCta && (
              <div className="mt-5 rounded-xl border border-white/20 bg-white/10 p-3">
                <p className="text-[13px] font-semibold text-white">{t('chat.reviewCompleteTitle')}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-white/70">
                  {visit?.providerName
                    ? t('chat.reviewCompleteBody', { provider })
                    : t('chat.reviewCompleteBodyGeneric')}
                </p>
                <button
                  onClick={reviewSummary}
                  className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-white text-[13px] font-semibold text-[#033C85] transition-opacity hover:opacity-90"
                >
                  <ClipboardCheck className="h-4 w-4" />
                  {t('chat.reviewSummaryCta')}
                </button>
              </div>
            )}

            <div className="mt-auto flex flex-col gap-3">
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors text-[12px] font-medium"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? t('chat.lightMode') : t('chat.darkMode')}
              </button>
              <Branding
                variant="inverse"
                className="border-t border-white/10 pt-4 text-left text-[11px]"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col min-w-0 min-h-0 bg-card">
          <header className="flex-shrink-0 border-b border-border">
            <div className="px-6 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <p className="text-sm font-semibold text-foreground">{t('chat.healthAssistant')}</p>
                {!isLoading && (
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-24 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#006EF9] to-[#02C7C3] rounded-full transition-all duration-700"
                        style={{ width: `${intakePct}%` }}
                      />
                    </div>
                    <span className="text-[11px] font-semibold text-muted-foreground">{intakePct}%</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowStopConfirm(true)}
                  className="flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-red-200 dark:border-red-900/40 text-red-500 text-[11px] font-semibold hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  aria-label={exitLabel}
                >
                  <StopCircle className="w-3 h-3" />
                  {exitLabel}
                </button>
              </div>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-8 xl:px-16 py-4">
            {isLoading ? (
              <ChatSkeleton />
            ) : (
              <MessageList messages={messages} isTyping={isSending} patientName={displayName} visit={visit} />
            )}
          </div>

          {voiceDropped && (
            <div className="px-8 xl:px-16 pt-3">
              <VoiceDroppedBanner
                onRestart={restartVoice}
                onDismiss={dismissVoiceDropped}
                className="max-w-2xl"
              />
            </div>
          )}

          <div className="px-8 xl:px-16 bg-card border-t border-border pt-2 pb-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <p className="text-[11px] text-muted-foreground">{t('chat.aiDisclaimerShort')}</p>
            </div>
            <div className="flex items-center gap-2 max-w-2xl">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && input.trim()) handleSend()
                }}
                placeholder={t('chat.inputPlaceholder')}
                className="flex-1 h-12 px-4 rounded-2xl border border-border bg-secondary dark:bg-input-bg text-[15px] text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
              />
              <ComposerButton
                hasText={input.trim().length > 0}
                voiceAvailable={voiceAvailable}
                onSend={handleSend}
                onVoice={requestVoice}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Stop/refuse confirmation ── */}
      {showStopConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowStopConfirm(false)} />
          <div className="relative z-10 w-full max-w-sm bg-card rounded-2xl border border-border shadow-2xl p-6 animate-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                <StopCircle className="h-5 w-5 text-red-500" />
              </div>
              <p className="text-base font-semibold text-foreground">{exitLabel}</p>
            </div>
            <p className="text-sm text-muted-foreground mb-5">{kiosk ? t('chat.handBackConfirm') : t('chat.stopChatConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowStopConfirm(false)}
                className="flex-1 h-10 rounded-xl border border-border bg-secondary text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                {t('chat.stopChatNo')}
              </button>
              <button
                onClick={confirmExit}
                className="flex-1 h-10 rounded-xl bg-red-500 text-sm font-semibold text-white hover:bg-red-600 transition-colors"
              >
                {kiosk ? t('chat.handBackYes') : t('chat.stopChatYes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Voice overlays ── */}
      {voiceState === 'requesting-permission' && (
        <MicPermissionModal denied={permissionDenied} onAllow={confirmVoice} onDismiss={cancelVoice} />
      )}
      {isOverlayVoiceState(voiceState) && (
        <VoiceModeOverlay
          state={voiceState}
          muted={muted}
          transcript={liveTranscript}
          caption={liveCaption}
          onMute={toggleMute}
          onSwitchToText={switchToText}
          onEnd={endVoice}
          onRetry={requestVoice}
        />
      )}
    </div>
  )
}
